'use client';

import { motion } from 'framer-motion';
import { useState } from 'react';

import { Image } from '@zoomit/primitives';
import { Box, cn, Flex, Link } from '@zoomit/primitives';
import type { MenuData, MenuItemType } from '@zoomit/web-kit';
import { BaseMediaUrl, IconRenderer } from '@zoomit/web-kit';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { canDisplayMenuItem } from '../../../helpers/menu/canDisplayMenuItem';
import { spreadIntoColumns } from '../../../helpers/menu/spreadIntoColumns';

type Props = {
  items: MenuData['items'];
  triggeredItem?: MenuItemType;
};

export const MenuSubItems = (props: Props) => {
  const { items } = props;

  const columns = spreadIntoColumns(items || []);

  const [triggeredSubItem, setTriggeredSubItem] = useState<MenuItemType>();

  const getIsActive = (title: string) => {
    if (!triggeredSubItem?.children) return false;
    if (triggeredSubItem.children.length === 0) return false;

    return triggeredSubItem?.title === title;
  };

  return (
    <motion.div
      key={`${props.triggeredItem?.title}-${props.items?.length}-subitems-${props.items.map((e) => e.title).join(',')}`}
      className={'flex min-w-48'}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {columns.map((items, colIdx) => (
        <Flex flexDirection={'column'}>
          {items.map((item, idx) => {
            const Tag = item.url ? Link : 'div';
            return (
              <Flex
                key={`${item.title}-${idx}-col-${colIdx}`}
                flexDirection={'column'}
                gap={1.6}
                className={'px-6 py-2'}
              >
                {canDisplayMenuItem.renderImage(item.displayType) && item.image && (
                  <Box className={'aspect-primary relative w-[200px]'}>
                    <Image
                      className={'rounded-sm object-cover'}
                      src={`${BaseMediaUrl}/${item.image.id}`}
                      alt={item.image.alt}
                      fill
                    />
                  </Box>
                )}
                <Flex gap={0.6} flexWrap={'nowrap'} alignItems={'center'}>
                  {canDisplayMenuItem.renderIcon(item.displayType) && item.icon && (
                    <IconRenderer icon={item.icon.content} fill={cssVar(theme.primary1)} />
                  )}
                  <Tag
                    className={cn(
                      'relative',
                      item.isHighlighted &&
                        'before:bg-primary1 pr-3 before:absolute before:top-1/2 before:right-0 before:h-5 before:w-1 before:-translate-y-1/2 before:rounded-sm',
                    )}
                    rel={item.noFollow ? 'nofollow' : ''}
                    href={item.url}
                    key={idx}
                    onPointerOver={() => setTriggeredSubItem(item)}
                  >
                    <Typography
                      className={'hover:text-primary1 leading-6'}
                      textColor={getIsActive(item.title) ? cssVar(theme.primary1) : cssVar(theme.text)}
                    >
                      {item.title}
                    </Typography>
                  </Tag>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      ))}
      {triggeredSubItem?.children && triggeredSubItem.children.length > 0 && (
        <MenuSubItems
          key={`${props.triggeredItem?.title}-${props.items?.length}`}
          items={triggeredSubItem.children}
          triggeredItem={triggeredSubItem}
        />
      )}
    </motion.div>
  );
};
